import { DealerClaim } from '@core/models/dealer.model';
import { DealerStateSelectors } from '@core/states/dealer/dealer-state.selectors';
import { Selector } from '@ngxs/store';
import { FormStatus } from '../../models/form-status.enum';
import { IdVerification } from '../../models/order-capture.model';
import { PhysicalVerificationType, VerificationType } from '../../modules/details/constants/Verification';
import { FORM_STATE_TOKEN } from './form.token';
import { FormModel, FormStateModel } from './form.types';
import { removeSpacesFromString } from '@app/core/utils';
import { BankIdVerification } from '@app/core/models/initialize-response.model';
import { CustomerSelectors } from '@app/sales/modules/details/states/customer.selectors';
import { ShoppingCartSelectors } from '@app/sales/states/shopping-cart/shopping-cart.selectors';

export class FormSelectors {
  @Selector([FORM_STATE_TOKEN])
  static getState(state: FormStateModel): FormStateModel {
    return state;
  }

  @Selector([FORM_STATE_TOKEN])
  static isFormDirty(state: FormStateModel): boolean {
    return Object.values(state).some(form => form.dirty);
  }

  @Selector([FORM_STATE_TOKEN])
  static isFormPending(state: FormStateModel): boolean {
    return Object.values(state).some(form => form.status === FormStatus.PENDING);
  }

  @Selector([FORM_STATE_TOKEN])
  static customerValidationForm(state: FormStateModel): FormModel {
    return state.customerValidationForm;
  }

  @Selector([FORM_STATE_TOKEN])
  static subscriptionForm(state: FormStateModel): FormModel {
    return state.subscriptionForm;
  }

  @Selector([FORM_STATE_TOKEN])
  static mbbForm(state: FormStateModel): FormModel {
    return state.mbbForm;
  }

  @Selector([FORM_STATE_TOKEN])
  static customerContactForm(state: FormStateModel) {
    return state.customerContactForm;
  }

  @Selector([FORM_STATE_TOKEN])
  static hasCustomerDetailsFormBeenInView(state: FormStateModel): boolean {
    return state.customerContactForm.hasBeenInView;
  }

  @Selector([FORM_STATE_TOKEN])
  static createCompetitionDealers(state: FormStateModel) {
    return state.createCompetitionForm.model.dealers;
  }

  @Selector([FORM_STATE_TOKEN])
  static subscriptionsFormHasError(state: FormStateModel): boolean {
    return state.subscriptionForm.status === FormStatus.INVALID;
  }

  @Selector([FORM_STATE_TOKEN])
  static mbbFormHasError(state: FormStateModel): boolean {
    return state.mbbForm.status === FormStatus.INVALID;
  }

  @Selector([FORM_STATE_TOKEN])
  static customerContactHasError(state: FormStateModel): boolean {
    return state.customerContactForm.status === FormStatus.INVALID;
  }

  @Selector([FORM_STATE_TOKEN])
  static twoFactorAuthenticationInfo(state: FormStateModel): { verificationType: string; verificationCode: string } {
    if (state.customerValidationForm.model.verificationType === VerificationType.TwoFA) {
      return {
        verificationType: state.customerValidationForm.model.verificationType,
        verificationCode: state.customerValidationForm.model.verificationCode,
      };
    }
    return null;
  }

  @Selector([
    FORM_STATE_TOKEN,
    FormSelectors?.getCustomerVerification,
    CustomerSelectors.bankIdVerification,
    CustomerSelectors.isCustomerVerifiedWithBankId,
    FormSelectors.isBankIdVerificationSelected,
    CustomerSelectors.isUnsuccessfullyAuthenticatedFromManny,
    ShoppingCartSelectors.hasNewMobilbytteAgreementInCartAndIsntBankIdVerified,
  ])
  static customerValidationHasError(
    state: FormStateModel,
    customerVerification: IdVerification,
    bankIdVerification: BankIdVerification,
    isCustomerVerifiedWithBankId: boolean,
    isBankIdVerificationSelected: boolean,
    isUnsuccessfullyAuthenticatedFromManny: boolean,
    hasNewMobilbytteAgreementInCartAndIsntBankIdVerified: boolean
  ): boolean {
    return (
      state.customerValidationForm.status === FormStatus.INVALID ||
      !customerVerification ||
      !customerVerification.verificationCode ||
      !(isBankIdVerificationSelected ? isCustomerVerifiedWithBankId : true) ||
      (bankIdVerification &&
        (!bankIdVerification.isSsnMatchingSsnInCreditCheck || isUnsuccessfullyAuthenticatedFromManny)) ||
      hasNewMobilbytteAgreementInCartAndIsntBankIdVerified
    );
  }

  @Selector([FormSelectors.customerContactHasError])
  static customerInfoHasError(customerContactHasError: boolean): boolean {
    return customerContactHasError;
  }

  @Selector([FORM_STATE_TOKEN])
  static tradeInForm(state: FormStateModel) {
    return state.tradeInForm;
  }

  @Selector([FORM_STATE_TOKEN])
  static tradeInConditionForm(state: FormStateModel) {
    return state.tradeInForm.model.condition;
  }

  @Selector([FORM_STATE_TOKEN, DealerStateSelectors.activeDealerClaims])
  static getCustomerVerification(formState: FormStateModel, claims: DealerClaim[]): IdVerification {
    const isQuestionOnlyVerification = DealerStateSelectors.isQuestionOnlyVerification(claims);

    const isBankIdVerification = formState.customerValidationForm.model.verificationType == VerificationType.bankID;
    if (isBankIdVerification) {
      return {
        verificationType: 'bankid',
        verificationCode: 'will be replaced',
      } as IdVerification;
    }

    const isTwoFactorAuthenticated = formState.customerValidationForm.model.verificationType == VerificationType.TwoFA;
    if (isTwoFactorAuthenticated) {
      return {
        verificationType: 'two_factor_authentication',
        verificationCode: formState.customerValidationForm.model.verificationCode,
      } as IdVerification;
    }

    if (isQuestionOnlyVerification) {
      return {
        verificationType: PhysicalVerificationType.controlQuestion,
        verificationCode: 'true',
      } as IdVerification;
    } else {
      const physicalVerificationType = formState.customerValidationForm.model.physicalVerificationType;
      switch (physicalVerificationType) {
        case PhysicalVerificationType.drivingLicense:
          return {
            verificationType: physicalVerificationType,
            verificationCode: removeSpacesFromString(formState.customerValidationForm.model.drivingLicense),
          } as IdVerification;
        case PhysicalVerificationType.idCard:
          return {
            verificationType: physicalVerificationType,
            verificationCode: removeSpacesFromString(formState.customerValidationForm.model.idCardNumber),
          } as IdVerification;
        case PhysicalVerificationType.pass:
          return {
            verificationType: physicalVerificationType,
            verificationCode: removeSpacesFromString(formState.customerValidationForm.model.passNumber),
          } as IdVerification;
        case PhysicalVerificationType.controlQuestion:
          return {
            verificationType: physicalVerificationType,
            verificationCode: 'true',
          } as IdVerification;
        default:
          return null;
      }
    }
  }

  @Selector([FORM_STATE_TOKEN])
  static isBankIdVerificationSelected(state: FormStateModel): boolean {
    if (!state?.customerValidationForm?.model) return false;
    return state.customerValidationForm.model.verificationType === VerificationType.bankID;
  }

  @Selector([FORM_STATE_TOKEN])
  static willBankIdOverrideCreditCheckFields(state: FormStateModel): boolean {
    return (
      state.customerValidationForm.model.bankIdMsisdn &&
      (state.customerAllowanceForm.model.ssn ||
        state.customerAllowanceForm.model.firstname ||
        state.customerAllowanceForm.model.lastname)
    );
  }
}
